import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { Api } from '../api'
import { ApiError, mayUseDeveloperMode } from '../api/types'
import type { ApiFailure, MeasureDoc, MeasureKind, MeasureRef, MeasureSource, SalesFilters } from '../api/types'
import { DevModeContext, type DevModeValue } from './DevModeContext'
import { FailureState } from './FailureState'
import { SlideOver } from './SlideOver'
import { EmptyState, Loading, Notice } from './ui'

export { Explain, useExplain } from './DevModeContext'

/**
 * What every figure, column and control on a report actually means.
 *
 * WHY THIS EXISTS. Whether a number is right in BUSINESS terms is not a
 * question the people who built the platform can answer alone. Until this
 * panel, checking it meant reading a five-hundred-line stored procedure — so
 * in practice nobody outside the repository could check anything. The text
 * comes from db/platform/029_measure_docs_sales.sql and its siblings, and CI
 * re-checks each formula against the live procedure on every build, so what is
 * shown here cannot quietly drift out of step with the code.
 *
 * WHAT AN ABSENT SECTION MEANS, AND WHY IT IS AMBIGUOUS ON PURPOSE. A section
 * is missing either because nothing was documented for that column or because
 * the switch governing it is off. THE SERVER decides, and strips what the flags
 * do not permit before sending — so an absent section was never on the wire and
 * this component could not reveal it if it tried. That is the point: the people
 * this feature is for are exactly the ones who open the network tab.
 *
 * ---------------------------------------------------------------------------
 * WHY A CONTEXT RATHER THAN PROPS
 *
 * The first version threaded a `devMode` boolean by hand into one component,
 * and that was fine while one grid was clickable. It does not survive contact
 * with the rest of the platform: Material Planning has six grids across ten
 * screens, the invoice ledger has twenty-five columns, and every KPI strip and
 * filter bar wants the same behaviour. Threading `api`, `roles`, `report`,
 * `devMode`, the open key, the setter and the request summary through thirty
 * components would be thirty chances to pass the wrong surface — and the symptom
 * of passing the wrong surface is a panel that confidently explains a different
 * column.
 *
 * So the page wraps itself once, and any descendant asks. The 8 KPI tiles are
 * the argument: they were seeded into the database in the first pass and never
 * became clickable, because making them clickable meant threading the state
 * into another component and nobody did.
 */

/**
 * Wrap a report page once. Everything under it can then offer an explanation
 * for any documented figure, column or control.
 *
 * It renders the panel itself, at the top of the subtree, so a header buried
 * six components deep does not have to own a slide-over.
 */
export function DevModeProvider({
  api,
  roles,
  report,
  request = {},
  children,
}: {
  api: Api
  roles: string[]
  report: string
  /**
   * The standing filters, as the page currently has them. Recomputed on every
   * render is fine and correct — the panel should describe the screen as it is
   * NOW, not as it was when the panel opened.
   */
  request?: Record<string, unknown>
  children: ReactNode
}) {
  const [refs, setRefs] = useState<Map<string, MeasureKind>>(() => new Map())
  const [open, setOpen] = useState<{ surface: string; key: string; extra?: Record<string, unknown> } | null>(null)
  /* Set only when the manifest call genuinely FAILED, never when it honestly
     returned nothing. See the notice below for why the distinction earns a
     piece of state. */
  const [broke, setBroke] = useState(false)

  const isAdmin = mayUseDeveloperMode(roles)

  /* THE MANIFEST, fetched once per report per page load.
     The flags are not fetched separately any more. They used to be, and then
     the manifest was added and the switch was being asked about twice — the
     manifest already comes back empty when developer mode is off, because the
     procedure applies the same two gates the panel does. One question, one
     answer, and no window in which the two disagree.
     Only someone who may use developer mode asks at all (System Admin, Master
     Admin, System Development: D5): the route refuses anyone else, so asking
     would be a guaranteed empty list on every page load. */
  useEffect(() => {
    if (!isAdmin) {
      setRefs(new Map())
      setBroke(false)
      return
    }
    let cancelled = false
    setBroke(false)
    api
      .measureRefs(report)
      .then((list: MeasureRef[]) => {
        if (cancelled) return
        setRefs(new Map(list.map((r) => [`${r.surface}/${r.key}`, r.kind])))
      })
      .catch(() => {
        if (cancelled) return
        setRefs(new Map())
        setBroke(true)
      })
    return () => {
      cancelled = true
    }
  }, [api, report, isAdmin])

  const value = useMemo<DevModeValue>(
    () => ({
      report,
      refs,
      request,
      open: (surface, key, extra) => setOpen({ surface, key, extra }),
    }),
    [report, refs, request],
  )

  return (
    <DevModeContext.Provider value={value}>
      {/* ONLY WHEN IT IS BROKEN, AND ONLY TO SOMEBODY WHO CAN FIX IT.
          A System Admin whose manifest call failed sees this; nobody else has
          the state set, so nobody else sees anything. It does not appear when
          developer mode is merely switched off -- that path returns an honest
          empty list and this stays false.

          It names the likely cause because the cause was not guessable from the
          screen: the API identity needs EXECUTE on plat.usp_List_Measure_Docs_MBI,
          and the day that grant was missing on the VM the report looked
          identical to one with the feature turned off. */}
      {broke && (
        <Notice tone="warn" title="Developer mode could not read the definitions">
          The switches are readable but the list of documented figures is not, so no column
          header will open a panel. This is usually a missing database grant —{' '}
          <span className="font-mono">plat.usp_List_Measure_Docs_MBI</span> needs EXECUTE for the
          API identity (<span className="font-mono">ops/vm/grant-vm-mi-to-clone.sql</span>).
          Nothing else on this report is affected.
        </Notice>
      )}
      {children}
      {open && (
        <MeasurePanel
          api={api}
          report={report}
          surface={open.surface}
          measureKey={open.key}
          kind={refs.get(`${open.surface}/${open.key}`)}
          request={{ ...request, ...open.extra }}
          onClose={() => setOpen(null)}
        />
      )}
    </DevModeContext.Provider>
  )
}

export function MeasurePanel({
  api,
  report,
  surface,
  measureKey,
  kind,
  /** What the browser asked the API for, so the panel can say what produced this screen. */
  request,
  onClose,
}: {
  api: Api
  report: string
  surface: string
  measureKey: string
  kind?: MeasureKind
  request?: Record<string, unknown>
  onClose: () => void
}) {
  const [doc, setDoc] = useState<MeasureDoc | null | 'loading'>('loading')

  /* A FAILURE IS NOT AN ABSENCE, and conflating them cost a real diagnosis.
     This used to .catch(() => setDoc(null)), so every error rendered as
     "Nothing documented for this column" -- and when the database was two
     versions behind the API and the procedure 500'd on a column it did not yet
     have, the screen calmly reported that the column had no definition. The
     platform is careful about this everywhere else: ApiFailure has distinct
     kinds precisely so "no permissions" and "no rows" cannot both render as an
     empty table. Only a genuine 404 means undocumented; anything else says so. */
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  useEffect(() => {
    let cancelled = false
    setDoc('loading')
    setFailure(null)
    api
      .measureDoc(report, surface, measureKey)
      .then((d) => !cancelled && setDoc(d))
      .catch((e) => {
        if (cancelled) return
        setFailure(
          e instanceof ApiError
            ? e.failure
            : { kind: 'error', title: 'The definition could not be read.' },
        )
      })
    return () => {
      cancelled = true
    }
  }, [api, report, surface, measureKey])

  const title = doc && doc !== 'loading' ? doc.displayName : measureKey
  const isFilter = kind === 'filter'

  /* THE HEADINGS FOLLOW THE KIND, and this is not decoration. Telling a
     reviewer "how it is calculated: matches f.Cust_Region" describes something
     the platform never did — nothing was calculated, a row was selected. A
     reviewer checking business logic reads these headings as claims. */
  const description = isFilter
    ? 'What this control does, which column it matches, and how the match is made.'
    : kind === 'dimension'
      ? 'What this column holds and which source column it is carried from.'
      : 'What this figure means, where it comes from, and how it is calculated.'

  return (
    <SlideOver title={title} description={description} onClose={onClose} width="lg">
      {failure ? (
        <FailureState failure={failure} />
      ) : doc === 'loading' ? (
        <Loading label="Reading the definition…" />
      ) : doc === null ? (
        <EmptyState
          title="Nothing documented for this column"
          detail="Either no definition has been written for it yet, or developer mode is switched off. Access › Developer mode has the switches."
        />
      ) : (
        <div className="flex flex-col gap-5">
          <Section title="What it means">
            <p className="text-(--color-ink-2)">{doc.definition}</p>
          </Section>

          {doc.notes && (
            /* Not a footnote. These are the caveats that decide whether a
               figure means what it appears to -- which rows are excluded, which
               column does not follow the currency toggle. A reviewer who reads
               the formula and misses these signs off something subtly wrong. */
            <Section title="Worth knowing">
              <p className="text-(--color-ink-2)">{doc.notes}</p>
            </Section>
          )}

          {doc.sources.length > 0 && (
            /* ONE BLOCK PER OBJECT, each carrying its own columns and its own
               join. The first version put the object in a box and the columns
               in grey underneath with nothing tying them together — which read
               well enough for a figure from one view and would have been
               unreadable for Material Planning, whose figures span four. */
            <Section title={isFilter ? 'What it matches against' : 'Where it comes from'}>
              <div className="flex flex-col gap-3">
                {doc.sources.map((s) => (
                  <SourceBlock key={s.ordinal} source={s} />
                ))}
              </div>
            </Section>
          )}

          {doc.formula && (
            <Section title={isFilter ? 'How it matches' : 'How it is calculated'}>
              <Mono>{doc.formula}</Mono>
            </Section>
          )}

          {doc.procName && (
            <Section title="What this screen asked for">
              <Mono>{doc.procName}</Mono>
              {doc.sourceFile && (
                <p className="mt-1 text-(length:--text-small) text-(--color-ink-3)">{doc.sourceFile}</p>
              )}
              {request && Object.keys(request).length > 0 && (
                <>
                  <p className="mt-3 eyebrow">The request this page sent</p>
                  <Mono>
                    {Object.entries(request)
                      .filter(([, v]) => v !== undefined && v !== null && v !== '')
                      .map(([k, v]) => `${k} = ${Array.isArray(v) ? v.join(', ') : String(v)}`)
                      .join('\n')}
                  </Mono>
                  {/* Said plainly, because the two are not the same thing and a
                      reviewer comparing them against the audit log would
                      otherwise be puzzled. */}
                  <p className="mt-2 text-(length:--text-small) text-(--color-ink-3)">
                    This is what the browser asked for. The procedure validates every value
                    again and may narrow it — an unrecognised period becomes YTD, and a page
                    size above 500 is capped — so what ran is this or something tighter,
                    never something wider.
                  </p>
                </>
              )}
            </Section>
          )}
        </div>
      )}
    </SlideOver>
  )
}

/**
 * One source object: what it is, what is read from it, and how it attaches.
 *
 * The join line is the reason this is a component rather than a line of text.
 * "LEFT JOIN dbo.D365_PostedSales_TRx ON cvc.Comp_Id = f.DATAAREAID" answers a
 * question a reviewer would otherwise have to ask, and on a four-source figure
 * it is the difference between a list of names and something that can be
 * checked.
 */
function SourceBlock({ source: s }: { source: MeasureSource }) {
  return (
    <div className="rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-mono text-(length:--text-small) font-semibold text-(--color-ink)">
          {s.object}
        </span>
        <RoleChip role={s.role} />
      </div>

      {s.columns && (
        <p className="mt-1.5 font-mono text-(length:--text-small) text-(--color-ink-2)">
          {s.columns}
        </p>
      )}

      {s.joinKind && s.joinTo && s.joinOn && (
        <p className="mt-1.5 border-t border-(--color-line) pt-1.5 font-mono text-(length:--text-small) text-(--color-ink-2)">
          <span className="font-semibold">{s.joinKind}</span> {s.joinTo}
          <br />
          <span className="text-(--color-ink-3)">ON </span>
          {s.joinOn}
        </p>
      )}

      {s.note && (
        <p className="mt-1.5 text-(length:--text-small) text-(--color-ink-3)">{s.note}</p>
      )}
    </div>
  )
}

/**
 * fact / dimension / security / options, as a chip.
 *
 * `security` gets the violet the platform uses everywhere for "this concerns
 * who may see what" — the same colour as the view-as banner and the sees-all-
 * data badge — so a reviewer meets a familiar signal rather than a new word.
 *
 * `options` shares that violet, because it is making the same kind of claim:
 * the menu you chose from was built from the rows YOU can see. Two people
 * comparing supplier lists and finding them different have found the design.
 */
function RoleChip({ role }: { role: string }) {
  const tone =
    role === 'security' || role === 'options'
      ? 'border-(--color-accent) text-(--color-accent)'
      : role === 'dimension'
        ? 'border-(--color-line-strong) text-(--color-ink-2)'
        : 'border-(--color-line-strong) text-(--color-ink-3)'
  const label =
    role === 'fact'
      ? 'the fact'
      : role === 'security'
        ? 'decides who sees it'
        : role === 'options'
          ? 'builds the menu, scoped to you'
          : role
  return (
    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-(length:--text-small) ${tone}`}>
      {label}
    </span>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <p className="eyebrow">{title}</p>
      <div className="mt-1">{children}</div>
    </section>
  )
}

/** Fixed-pitch, wrapping, scrollable sideways rather than clipped. */
function Mono({ children }: { children: React.ReactNode }) {
  return (
    <pre className="scroll-slim overflow-x-auto whitespace-pre-wrap rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-2 font-mono text-(length:--text-small) text-(--color-ink)">
      {children}
    </pre>
  )
}

/**
 * The standing filters, as the panel wants to show them: only what is actually
 * set, under the names a person would recognise rather than the query-string
 * abbreviations.
 */
export function salesRequestSummary(
  filters: SalesFilters,
  extra: Record<string, unknown> = {},
): Record<string, unknown> {
  return {
    ...extra,
    ...(filters.company?.length ? { Companies: filters.company } : {}),
    ...(filters.bu ? { 'Business unit': filters.bu } : {}),
    ...(filters.bt ? { 'Business team': filters.bt } : {}),
    ...(filters.sp ? { Salesperson: filters.sp } : {}),
    ...(filters.sup?.length ? { Supplier: filters.sup.join(', ') } : {}),
    ...(filters.reg ? { 'Customer region': filters.reg } : {}),
    ...(filters.cust ? { 'Customer contains': filters.cust } : {}),
  }
}
